import { Link } from "react-router-dom";
import { ArrowUpRight, Quote, Linkedin, BrainCircuit, Rocket, RefreshCcw, Compass, Presentation, UserCheck, TrendingUp, Layers } from "lucide-react";
import { Reveal, SectionHeading } from "../components/Motion";
import { useContent } from "../lib/content";

const ICONS = { BrainCircuit, Rocket, RefreshCcw, Compass, Presentation, UserCheck, TrendingUp, Layers };

export const ServicesSection = ({ full = false, hideHeading = false }) => {
  const { services } = useContent();
  const list = services;

  return (
    <section data-testid="services-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      {!hideHeading && (
        <SectionHeading
          overline="Services"
          title={<>How I help organizations <span className="text-gradient">move faster</span></>}
          description="From focused executive workshops to fractional AI advisory - engagements scoped around your business problem, not a services menu."
        />
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {list.map((s, i) => {
          const Icon = ICONS[s.icon] || Compass;
          return (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <div data-testid={`service-card-${i}`} className="card-hover group rounded-2xl border border-border bg-card p-8 h-full hover:border-secondary/40">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-violet-600/15 to-blue-600/15 border border-border flex items-center justify-center text-secondary">
                  <Icon size={20} strokeWidth={1.6} />
                </div>
                <h3 className="mt-6 font-display text-lg font-medium tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.description}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      {!full && (
        <Reveal className="mt-14">
          <Link to="/services" data-testid="services-more-link" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline">
            All services & engagement models <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      )}
    </section>
  );
};

export const TestimonialsSection = () => {
  const { testimonials } = useContent();
  const featured = testimonials.find((t) => t.featured);
  const rest = testimonials.filter((t) => !t.featured);
  return (
    <section data-testid="testimonials-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      <SectionHeading
        overline="Testimonials"
        title="What partners & clients say"
      />
      {featured && (
        <Reveal>
          <figure data-testid="testimonial-featured" className="card-hover rounded-2xl border border-secondary/30 bg-gradient-to-br from-violet-600/10 via-card to-blue-600/10 p-10 md:p-14 mb-8 relative overflow-hidden">
            <Quote size={30} className="text-secondary/60" strokeWidth={1.5} />
            <blockquote className="mt-6 text-lg md:text-2xl leading-relaxed text-foreground/90 font-display max-w-4xl">
              "{featured.quote}"
            </blockquote>
            <figcaption className="mt-10 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="font-display font-medium text-base">{featured.name}</p>
                <p className="text-sm text-muted-foreground mt-1">{featured.role} · {featured.company}</p>
              </div>
              {featured.linkedin && (
                <a href={featured.linkedin} target="_blank" rel="noreferrer" data-testid="testimonial-linkedin-link" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline shrink-0">
                  <Linkedin size={14} /> via LinkedIn
                </a>
              )}
            </figcaption>
          </figure>
        </Reveal>
      )}
      <div className="grid md:grid-cols-3 gap-5 md:gap-6">
        {rest.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.1}>
            <figure data-testid={`testimonial-card-${i}`} className="card-hover rounded-2xl border border-border bg-card p-6 h-full flex flex-col hover:border-secondary/40">
              <Quote size={18} className="text-secondary/60" strokeWidth={1.5} />
              <blockquote className="mt-4 text-sm leading-relaxed text-foreground/80 flex-1">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-border">
                <p className="font-display font-medium text-sm">{t.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{t.role} · {t.company}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
